import { observer } from 'mobx-react-lite'
import { type ReactNode, type RefObject, isValidElement } from 'react'
import classNames from 'classnames'
import {
  EmptyCell,
  type IColumn,
  type IRowDefault,
  type ITableProps,
  TableCell,
  CellWithoutContent,
} from 'shared/ui/TableNew'
import styles from 'shared/ui/TableNew/ui/TableRow/styles.module.scss'
import { DayjsFormats, isDateString, isEmptyOrSpacesString } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'

type IColumnCellProps<TRow extends IRowDefault> = {
  row: TRow
  column: IColumn<TRow>
  getRowLink: ITableProps<TRow>['getRowLink']
  onRowClick: ITableProps<TRow>['onRowClick']
  defaultWidthColumn: ITableProps<TRow>['defaultWidthColumn']
  defaultMinWidthColumn: ITableProps<TRow>['defaultMinWidthColumn']
  defaultMaxWidthColumn: ITableProps<TRow>['defaultMaxWidthColumn']
  defaultColumnCellStyle?: ITableProps<TRow>['defaultColumnCellStyle']
  isExpandedChild?: boolean
  isCursor?: boolean
  colSpan?: number
  children: (ref: RefObject<HTMLDivElement>) => ReactNode
}

export const ColumnCell = observer(
  <TRow extends IRowDefault>({
    column,
    row,
    getRowLink,
    onRowClick,
    isExpandedChild,
    isCursor,
    colSpan,
    defaultWidthColumn,
    defaultMinWidthColumn,
    defaultMaxWidthColumn,
    children,
    defaultColumnCellStyle,
  }: IColumnCellProps<TRow>) => {
    const getLink = () => {
      if (column.disableLink) return
      if (column.getIsDisableLink?.(row)) return
      if (column.link) return column.link

      return getRowLink?.(row)
    }
    const link = getLink()
    const isPointer = () => {
      if (row.isDisabled || row.origin?.isDisabled) return false
      if (column.onCheckCellPointer) {
        return column.onCheckCellPointer(row)
      }

      return Boolean(column.onCellClick || onRowClick || link || isCursor)
    }
    const isCellClick = () => {
      if (column.onCellClick) {
        return () => column.onCellClick?.(row)
      }
      if (onRowClick) {
        return () => onRowClick(row)
      }
    }

    return (
      <TableCell
        key={String(column.field)}
        ariaLabel={column.ariaLabel}
        className={classNames(
          { [styles.disabled]: row.isDisabled || row.origin?.isDisabled },
          column.className
        )}
        width={column.width || defaultWidthColumn}
        maxWidth={column.maxWidth || defaultMaxWidthColumn}
        minWidth={column.minWidth || defaultMinWidthColumn}
        padding={column.padding}
        link={link}
        isPointer={isPointer()}
        onCellClick={isCellClick()}
        style={{
          paddingLeft: isExpandedChild ? column.paddingInExpand : undefined,
          cursor: isPointer() ? 'pointer' : 'default',
          ...defaultColumnCellStyle,
        }}
        colSpan={colSpan}
        isOverflowInitial={column.isOverflowInitial}
      >
        {(ref) => children(ref)}
      </TableCell>
    )
  }
)

type IDefaultRenderRowCellProps<TRow extends IRowDefault> = {
  row: TRow
  column: IColumn<TRow>
  hideExpandColumnsIds?: ITableProps<TRow>['hideExpandColumnsIds']
  isHover: boolean
  isCheckDate?: boolean
  refTable: RefObject<HTMLDivElement>
  refCell: RefObject<HTMLDivElement>
}

export const DefaultRenderRowCell = observer(
  <TRow extends IRowDefault>({
    row,
    column,
    hideExpandColumnsIds,
    isHover,
    isCheckDate,
    refTable,
    refCell,
  }: IDefaultRenderRowCellProps<TRow>) => {
    if (hideExpandColumnsIds?.includes(column.field)) return null
    if (column.renderRowCell) {
      const content = column.renderRowCell(row, column, {
        hidden: styles.hidedElem,
        isHover: isHover,
        refTable: refTable,
        refCell: refCell,
      })

      if (content === null || content === undefined || content === '') {
        return <EmptyCell />
      }

      return <>{content}</>
    }

    if (column.isCellWithoutContent) {
      const isWithoutContent = column.isCellWithoutContent(row)

      if (isWithoutContent) return <CellWithoutContent />
    }

    const cellValue = row.fields[column.field]

    if (typeof cellValue === 'number') {
      return <>{cellValue}</>
    }

    if (!cellValue || isEmptyOrSpacesString(String(cellValue))) {
      return <EmptyCell />
    }

    const isDateValue = isDateString(String(cellValue))

    if (isDateValue && isCheckDate) {
      return <>{uiStore.dayjs(String(cellValue)).format(DayjsFormats.fullWithAtDash2)}</>
    }

    return isValidElement(cellValue) || typeof cellValue === 'string' ? (
      <>{cellValue}</>
    ) : (
      <>Error load data</>
    )
  }
)
